import { FunctionComponent, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { Radio } from "@/components/Radio/Radio";
import { useSnackBar } from "@/utils/useSnackbar";

import { useLazyGetSalesforceOAuthConnectUrlQuery } from "@/pages/Private/redux/salesforce/salesforce.api";

import {
	SALESFORCE_PRODUCTION_HOST,
	SALESFORCE_SANDBOX_HOST,
	SalesforceConnectionFormValues,
	SalesforceEnvironment,
	SalesforceOAuthStatusResponse,
	createSalesforceConnectionSchema,
} from "../schema/salesforce";

type Ts = (key: string) => string;

interface SalesforceConnectionTabProps {
	oauthStatus?: SalesforceOAuthStatusResponse;
	isDisconnecting: boolean;
	onOpenFieldMapping: () => void;
	onRequestDisconnect: () => void;
	ts: Ts;
}

const getBackendMessage = (error: unknown): string | undefined => {
	if (typeof error === "object" && error !== null && "data" in error) {
		const data = (error as { data?: { message?: string | string[] } }).data;

		if (Array.isArray(data?.message)) {
			return data.message[0];
		}

		if (typeof data?.message === "string") {
			return data.message;
		}
	}

	return undefined;
};

/**
 * Connection tab of the Salesforce settings.
 *
 * The environment picker has no Dynamics equivalent to copy. Salesforce has no single OAuth
 * host, and a refresh token is scoped to the host that issued it — refreshing a sandbox token
 * against `login.salesforce.com` fails with `invalid_grant` — so the environment is chosen
 * before the redirect and replayed on every refresh thereafter.
 *
 * `MY_DOMAIN` is the case an enum of hosts cannot express: the host is free text. Its rule is
 * the backend's own (`normalizeMyDomainOrigin`), mirrored in Zod so a typo is a field error
 * rather than a round-trip, and when the backend still rejects it its message is surfaced
 * verbatim.
 */
export const SalesforceConnectionTab: FunctionComponent<SalesforceConnectionTabProps> = ({
	oauthStatus,
	isDisconnecting,
	onOpenFieldMapping,
	onRequestDisconnect,
	ts,
}) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [getOAuthConnectUrl, { isLoading: isLoadingOAuthUrl }] =
		useLazyGetSalesforceOAuthConnectUrlQuery();

	const schema = useMemo(
		() =>
			createSalesforceConnectionSchema({
				required: ts("oauth.myDomainRequired"),
				invalid: ts("oauth.myDomainInvalid"),
			}),
		[ts]
	);

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<SalesforceConnectionFormValues>({
		resolver: zodResolver(schema),
		mode: "onChange",
		defaultValues: { environment: SalesforceEnvironment.PRODUCTION, myDomain: "" },
	});

	const environment = watch("environment");
	const myDomain = watch("myDomain") ?? "";
	const isMyDomain = environment === SalesforceEnvironment.MY_DOMAIN;

	const isConnected = oauthStatus?.connected ?? false;

	const environmentOptions = [
		{
			value: SalesforceEnvironment.PRODUCTION,
			label: ts("oauth.environmentProduction"),
			host: SALESFORCE_PRODUCTION_HOST,
			hint: ts("oauth.environmentProductionHint"),
			testId: "salesforce-environment-production",
		},
		{
			value: SalesforceEnvironment.SANDBOX,
			label: ts("oauth.environmentSandbox"),
			host: SALESFORCE_SANDBOX_HOST,
			hint: ts("oauth.environmentSandboxHint"),
			testId: "salesforce-environment-sandbox",
		},
		{
			value: SalesforceEnvironment.MY_DOMAIN,
			label: ts("oauth.environmentMyDomain"),
			// No fixed host — that is exactly what makes this the third option.
			host: null,
			hint: ts("oauth.environmentMyDomainHint"),
			testId: "salesforce-environment-my-domain",
		},
	];

	const handleConnect = handleSubmit(async (values) => {
		try {
			const result = await getOAuthConnectUrl({
				environment: values.environment,
				// Sent only for MY_DOMAIN — the backend ignores it otherwise, and a stale value
				// left behind by switching environments must not travel with the request.
				myDomain:
					values.environment === SalesforceEnvironment.MY_DOMAIN ? values.myDomain : undefined,
			}).unwrap();

			window.location.href = result.url;
		} catch (error) {
			handleOpenSnackbar(getBackendMessage(error) ?? ts("oauth.connectError"));
		}
	});

	if (isConnected) {
		return (
			<div className="flex flex-col gap-4">
				{renderMessageSnackbar()}
				<div className="flex items-center gap-3 rounded-lg border border-status-success bg-green-50 p-4">
					<Icon className="h-5 w-5 text-status-success" icon={faCircleCheck} />
					<div className="flex flex-col">
						<span className="text-sm font-semibold text-status-success">
							{ts("oauth.connectedViaOAuth")}
						</span>
						{oauthStatus?.apiDomain && (
							<span className="text-xs text-text-meta" data-testid="salesforce-instance-url">
								{ts("oauth.instanceUrl")}: {oauthStatus.apiDomain}
							</span>
						)}
						{oauthStatus?.loginHost && (
							<span className="text-xs text-text-meta" data-testid="salesforce-login-host">
								{ts("oauth.loginHost")}: {oauthStatus.loginHost}
							</span>
						)}
						{oauthStatus?.salesforceOrgId && (
							<span className="text-xs text-text-meta">
								{ts("oauth.salesforceOrgId")}: {oauthStatus.salesforceOrgId}
							</span>
						)}
						{oauthStatus?.salesforceUserId && (
							<span className="text-xs text-text-meta">
								{ts("oauth.salesforceUserId")}: {oauthStatus.salesforceUserId}
							</span>
						)}
						<span className="text-xs text-text-meta">{ts("oauth.tokenRefresh")}</span>
					</div>
				</div>
				<div className="flex flex-row gap-2">
					<div className="flex flex-wrap gap-2">
						<Button
							color={ButtonColor.DANGER_SECONDARY}
							isLoading={isDisconnecting}
							size={ButtonSize.S}
							title={ts("disconnect")}
							onClick={onRequestDisconnect}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION}
							size={ButtonSize.S}
							title={ts("configureFieldMapping")}
							onClick={onOpenFieldMapping}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<form className="flex flex-col gap-3" onSubmit={handleConnect}>
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta">{ts("oauth.environmentDescription")}</div>

			<fieldset className="flex flex-col gap-2" data-testid="salesforce-environment-picker">
				<legend className="mb-1 text-sm font-medium text-text">{ts("oauth.environment")}</legend>
				{environmentOptions.map((option) => (
					<div key={option.value} className="flex flex-col">
						<Radio
							isChecked={environment === option.value}
							name="environment"
							testId={option.testId}
							value={option.value}
							onChange={() =>
								setValue("environment", option.value, {
									shouldValidate: true,
									shouldDirty: true,
								})
							}
						>
							{option.label}
						</Radio>
						<span className="ml-7 text-xs text-text-meta">
							{option.host ? `${option.host} — ${option.hint}` : option.hint}
						</span>
					</div>
				))}
			</fieldset>

			{isMyDomain && (
				<div className="w-full max-w-[420px]">
					<InputField
						error={errors.myDomain?.message}
						handleChange={(event) =>
							setValue("myDomain", event.target.value, {
								shouldValidate: true,
								shouldDirty: true,
							})
						}
						information={ts("oauth.myDomainHelp")}
						label={ts("oauth.myDomainLabel")}
						name="myDomain"
						placeholder={ts("oauth.myDomainPlaceholder")}
						showError={Boolean(errors.myDomain)}
						testId="salesforce-my-domain-input"
						touched={Boolean(errors.myDomain)}
						value={myDomain}
					/>
					{errors.myDomain?.message && (
						<span className="mt-1 block text-xs text-status-error" role="alert">
							{errors.myDomain.message}
						</span>
					)}
				</div>
			)}

			<div className="max-w-[250px]">
				<Button
					color={ButtonColor.ACTION}
					disabled={!isValid}
					isLoading={isLoadingOAuthUrl}
					size={ButtonSize.S}
					testId="salesforce-connect-button"
					title={ts("oauth.connectWithSalesforce")}
					type="submit"
				/>
			</div>
		</form>
	);
};
